import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Plus, Upload, Search, Edit3, Trash2, Users, Download, CheckCircle, XCircle } from "lucide-react";
import { formatCrewPhone } from "../utils/phone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = () => ({ withCredentials: true });
const DEPTS = ["", "Camera", "Grip", "Electric", "Sound", "Art Dept", "Hair & Makeup", "Wardrobe", "Catering", "Transportation", "Production", "Locations", "Script", "VFX", "Talent", "Other"];

// Common film-industry positions grouped by department (for autocomplete hints)
const POSITIONS_BY_DEPT = {
  "Camera": ["Director of Photography", "1st AC", "2nd AC", "DIT", "Loader", "Steadicam Op", "B-Cam Operator", "Camera PA"],
  "Grip": ["Key Grip", "Best Boy Grip", "Dolly Grip", "Grip", "Rigging Grip"],
  "Electric": ["Gaffer", "Best Boy Electric", "Rigging Gaffer", "Electrician", "Generator Op"],
  "Lighting": ["Gaffer", "Best Boy Electric", "Rigging Gaffer", "Electrician", "Generator Op"],
  "Sound": ["Production Sound Mixer", "Boom Op", "Utility Sound", "Sound PA"],
  "Art Department": ["Production Designer", "Art Director", "Set Decorator", "Prop Master", "On-Set Dresser", "Buyer", "Art PA"],
  "Art Dept": ["Production Designer", "Art Director", "Set Decorator", "Prop Master", "On-Set Dresser", "Buyer", "Art PA"],
  "Hair & Makeup": ["Key Makeup", "Makeup Artist", "Key Hair", "Hair Stylist", "SFX Makeup"],
  "Wardrobe": ["Costume Designer", "Costume Supervisor", "Key Costumer", "Costumer", "Set Costumer"],
  "Locations": ["Location Manager", "Assistant Location Manager", "Location Scout", "Location Assistant"],
  "Transportation": ["Transportation Coordinator", "Transportation Captain", "Driver"],
  "Catering": ["Craft Service", "Chef", "Assistant Chef", "Server"],
  "Production": ["Producer", "Line Producer", "UPM", "Production Coordinator", "APOC", "Production Secretary", "Production PA", "1st AD", "2nd AD", "2nd 2nd AD", "Set PA"],
  "Cast": ["Lead Actor", "Supporting Actor", "Day Player", "Background", "Stand-In", "Photo Double"],
  "Stunts": ["Stunt Coordinator", "Stunt Performer", "Stunt Rigger"],
  "VFX": ["VFX Supervisor", "VFX Producer", "On-Set VFX", "Data Wrangler"],
  "Script": ["Script Supervisor"],
  "Other": [],
};
function positionsFor(dept) {
  if (!dept) return [];
  return POSITIONS_BY_DEPT[dept] || [];
}

const BLANK = { name: "", phone: "", email: "", notes: "" };
const isTalentDept = (d) => (d || "").trim().toLowerCase() === "talent";

export default function CrewManagement() {
  const [crew, setCrew] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "add" | crew object
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef();

  useEffect(() => { fetchCrew(); fetchProjects(); }, []);

  const fetchCrew = async () => {
    try {
      const { data } = await axios.get(`${API}/crew`, ax());
      setCrew(data);
    } finally { setLoading(false); }
  };

  const fetchProjects = async () => {
    try {
      const { data } = await axios.get(`${API}/projects`, ax());
      setProjects(data);
    } catch {}
  };

  const openAdd = () => { setForm(BLANK); setModal("add"); };
  const openEdit = (m) => {
    setForm({
      name: m.name,
      phone: formatCrewPhone(m.phone || ""),
      email: m.email || "",
      notes: m.notes || "",
    });
    setModal(m);
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (modal === "add") {
        const { data } = await axios.post(`${API}/crew`, form, ax());
        setCrew([...crew, data]);
      } else {
        const { data } = await axios.put(`${API}/crew/${modal.id}`, form, ax());
        setCrew(crew.map(c => c.id === modal.id ? data : c));
      }
      setModal(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Remove this crew member?")) return;
    await axios.delete(`${API}/crew/${id}`, ax());
    setCrew(crew.filter(c => c.id !== id));
  };

  const handleCSV = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/crew/import-csv`, fd, { ...ax(), headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      await fetchCrew();
    } catch (e) {
      setImportResult({ error: e.response?.data?.detail || "Import failed" });
    } finally { setImporting(false); e.target.value = ""; }
  };

  const downloadTemplate = () => {
    const csv = "Name,Role,Phone,Email\nJane Smith,Director of Photography,555-0101,jane@example.com\nBob Jones,Grip,555-0102,bob@example.com";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "crew_template.csv";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const filtered = crew.filter((member) =>
    !search
    || member.name.toLowerCase().includes(search.toLowerCase())
    || (member.email || "").toLowerCase().includes(search.toLowerCase()),
  );

  // Aggregate department names: union of every project's `departments` list,
  // plus any legacy department/role value already used on existing crew.
  const allProjectDepts = projects.reduce((acc, p) => {
    (p.departments || []).forEach(d => { if (d) acc.add(d); });
    return acc;
  }, new Set());
  crew.forEach(c => { if (c.department) allProjectDepts.add(c.department); });
  const availableDepts = [...allProjectDepts].sort();

  const depts = [...new Set(crew.map(c => c.department || c.role).filter(Boolean))].sort();

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-amber-400/80 uppercase tracking-widest mb-1 font-mono">Database</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white font-[Outfit] tracking-tight">Company People Database</h1>
          <p className="text-gray-500 text-sm mt-1">{crew.length} people</p>
        </div>
        <div className="flex gap-3">
          <button onClick={downloadTemplate}
            className="flex items-center gap-2 border border-[#28303F] text-gray-400 hover:text-white px-3 py-2 rounded-lg text-sm transition-all">
            <Download size={15}/> Template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={importing}
            className="flex items-center gap-2 border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 px-4 py-2 rounded-lg text-sm transition-all">
            <Upload size={15}/> {importing ? "Importing..." : "Import CSV"}
          </button>
          <input ref={fileRef} type="file" accept=".csv" onChange={handleCSV} data-testid="crew-csv-upload-input" className="hidden" />
          <button data-testid="crew-add-manual-btn" onClick={openAdd}
            className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-bold px-4 py-2 rounded-lg text-sm transition-all">
            <Plus size={15}/> Add Person
          </button>
        </div>
      </div>

      {/* Import result */}
      {importResult && (
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${importResult.error ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-green-500/10 border-green-500/30 text-green-400"}`}>
          {importResult.error ? <XCircle size={18}/> : <CheckCircle size={18}/>}
          <span className="text-sm">
            {importResult.error || `Imported ${importResult.imported} new, updated ${importResult.updated} existing.${importResult.errors?.length ? ` ${importResult.errors.length} errors.` : ""}`}
          </span>
          <button onClick={() => setImportResult(null)} className="ml-auto text-gray-500 hover:text-white">&times;</button>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" />
          <input data-testid="crew-search-input" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email..."
            className="w-full bg-[#1A1F29] border border-[#28303F] rounded-lg pl-9 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-amber-500 text-sm" />
        </div>
      </div>

      {/* CSV Format hint */}
      <div className="bg-[#1E2530] border-l-4 border-amber-500 p-3 rounded-r-lg text-xs text-gray-400">
        CSV format: <code className="text-amber-400 font-mono">Name, Phone, Email</code> — project job titles are set inside each project.
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-amber-400 animate-pulse">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#28303F] rounded-2xl">
          <Users size={36} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">{crew.length === 0 ? "No crew members yet" : "No results"}</p>
          <p className="text-gray-600 text-sm mt-1">Add crew manually or import a CSV file</p>
        </div>
      ) : (
        <div className="bg-[#14181F] border border-[#28303F] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#28303F] text-xs text-gray-500 uppercase tracking-wider">
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.id} data-testid="crew-table-row"
                  className={`border-b border-[#28303F]/50 hover:bg-[#1A1F29] transition-colors ${i % 2 === 0 ? "" : "bg-[#14181F]/50"}`}>
                  <td className="px-5 py-3 font-medium text-white">{m.name}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{m.phone || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{m.email || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(m)}
                        className="p-1.5 text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 rounded transition-all" title="Edit">
                        <Edit3 size={14}/>
                      </button>
                      <button onClick={() => deleteMember(m.id)}
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-all" title="Delete">
                        <Trash2 size={14}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#14181F] border border-[#28303F] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-[#28303F]">
              <h3 className="font-bold text-white font-[Outfit]">{modal === "add" ? "Add Crew Member" : "Edit Crew Member"}</h3>
              <button onClick={() => setModal(null)} className="text-gray-500 hover:text-white text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Full Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Phone</label>
                  <input value={form.phone} onChange={e => setForm({ ...form, phone: formatCrewPhone(e.target.value) })} type="tel"
                    className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1.5 uppercase tracking-wider">Email</label>
                  <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email"
                    className="w-full bg-[#0D0F12] border border-[#28303F] rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setModal(null)}
                  className="flex-1 border border-[#28303F] text-gray-400 hover:text-white py-2.5 rounded-lg text-sm transition-colors">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-2.5 rounded-lg text-sm transition-all">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
